import { userFacingErrorMessage } from "@/utils/extract-rtk-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "components/ui/form";
import { Input } from "components/ui/input";
import { toast } from "components/ui/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { MESSAGES } from "@/pages/conciliation/stores/utils/messages";
import { useCreateDivergenceMutation } from "@/services/divergence";
import { FormSchema } from "./validate-form";

const formatCurrency = (value: string) => {
  const cleanedValue = value.replace(/[^0-9,-]/g, "");
  const isNegative = cleanedValue.startsWith("-");
  const numericValue = parseFloat(cleanedValue.replace(/[^0-9]/g, "")) / 100;

  const formattedValue = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);

  return (isNegative ? "-" : "") + formattedValue.replace("R$", "").trim();
};

const parseCurrency = (value: string) => {
  const isNegative = value.startsWith("-");
  const cleanedValue = value.replace(/[^0-9,]/g, "").replace(",", ".");
  const numericValue = parseFloat(cleanedValue);
  return isNegative ? -numericValue : numericValue;
};

export default function CreateDivergence({
  id_transaction,
}: {
  id_transaction: string;
  refetch?: () => void;
}) {
  const { handleModal } = useDialogModal();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });
  const [createDivergence] = useCreateDivergenceMutation();

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      const floatValue = parseCurrency(data.amount);

      await createDivergence({
        name: data?.name || "",
        amount: floatValue || 0,
        conciliation_id: +id_transaction,
      }).unwrap();
      toast(MESSAGES.CREATE.SUCCESS);
      handleModal({
        isOpen: false,
      });
      form.reset();
    } catch (error) {
      toast({
        ...MESSAGES.CREATE.ERROR,
        description: userFacingErrorMessage(
          error,
          MESSAGES.CREATE.ERROR.description
        ),
      });
    }
  }
  return (
    <>
      <div className="grid gap-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <Input
                      {...field}
                      hasError={!!form.formState.errors.name}
                      name="justification"
                      placeholder="Nome da divergência "
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative flex items-center !w-full">
                      <p className="absolute left-3 text-black font-bold text-[14px]">
                        R$
                      </p>
                      <div className="w-full">
                        <Input
                          {...field}
                          hasError={!!form.formState.errors.amount}
                          name="amount"
                          type="text"
                          placeholder="Valor da divergência"
                          className="pl-10 w-full"
                          onChange={(e) => {
                            const { value } = e.target;
      
                            if (value === "-") {
                              field.onChange(value);
                              return;
                            }
                          
                            const formattedValue = formatCurrency(value);
                            field.onChange(formattedValue);
                            form.setValue("amount", formattedValue);
                          }}
                        />
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="w-full flex gap-4 justify-end">
                <button
                  className="self-end w-max px-4 py-2 border border-black/20 flex items-center gap-2 rounded-md
                    text-black bg-white
                    transition-all duration-300 ease-in-out
                    hover:bg-[#2B0C1A] hover:text-white hover:border-transparent
                    "
                  onClick={() =>
                    handleModal({
                      isOpen: false,
                    })
                  }
                >
                  Voltar
                </button>
                <Button
                  type="submit"
                  variant="default"
                  isLoading={false}
                  disabled={form.formState.isSubmitting}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
